import { useEffect, useState, type ReactNode } from "react";
import { HiOutlineX } from "react-icons/hi";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export type FilePreviewKind = "image" | "pdf" | "other";

const IMAGE_EXTENSIONS = new Set([
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "svg",
  "bmp",
  "avif",
]);

export function detectPreviewKind(name: string, mimeType?: string): FilePreviewKind {
  const ext = (name.split(".").pop() ?? "").toLowerCase();
  if (mimeType?.startsWith("image/") || IMAGE_EXTENSIONS.has(ext)) return "image";
  if (mimeType === "application/pdf" || ext === "pdf") return "pdf";
  return "other";
}

type FilePreviewModalProps = {
  open: boolean;
  onClose: () => void;
  name: string;
  url?: string | null;
  kind?: FilePreviewKind;
  pdfMaxWidth?: number;
  /** When provided, renders children in the body instead of the built-in image/pdf/fallback. */
  children?: ReactNode;
};

export default function FilePreviewModal({
  open,
  onClose,
  name,
  url,
  kind,
  pdfMaxWidth = 800,
  children,
}: FilePreviewModalProps) {
  const [numPages, setNumPages] = useState<number | null>(null);

  useEffect(() => {
    if (!open) setNumPages(null);
  }, [open, url]);

  if (!open) return null;

  const resolvedKind: FilePreviewKind = kind ?? (url ? detectPreviewKind(name) : "other");

  const pageWidth = Math.min(
    pdfMaxWidth,
    typeof window !== "undefined" ? window.innerWidth - 48 : pdfMaxWidth,
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="relative flex h-[95vh] sm:h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-3 py-3 sm:px-4">
          <span className="truncate pr-2 text-xs font-medium text-[#222] sm:text-sm">{name}</span>
          <div className="flex items-center gap-3 shrink-0">
            {url ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#2E3037] underline underline-offset-2"
              >
                Open in new tab
              </a>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="flex size-10 shrink-0 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222] sm:size-8 touch-manipulation"
              aria-label="Close preview"
            >
              <HiOutlineX className="size-5" strokeWidth={2} />
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-auto bg-[#fafafa] p-2 sm:p-4 flex justify-center">
          {children ? (
            children
          ) : resolvedKind === "image" && url ? (
            <img
              src={url}
              alt={name}
              className="max-w-full max-h-full object-contain"
            />
          ) : resolvedKind === "pdf" && url ? (
            <Document
              file={url}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              loading={<p className="text-sm text-[#6d6d6d]">Loading PDF…</p>}
              error={<p className="text-sm text-red-600">Failed to load PDF.</p>}
              className="flex flex-col items-center gap-4"
            >
              {numPages != null &&
                Array.from({ length: numPages }, (_, i) => (
                  <Page
                    key={i + 1}
                    pageNumber={i + 1}
                    width={pageWidth}
                    renderTextLayer
                    renderAnnotationLayer
                  />
                ))}
            </Document>
          ) : (
            <div className="p-6 text-center">
              <p className="text-sm text-[#6D6D6D]">
                Preview is not available for this file type.
              </p>
              {url ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-2 text-xs text-[#2E3037] underline underline-offset-2"
                >
                  Open in new tab
                </a>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
